import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useInView } from '@/hooks/use-in-view'

interface CountUpProps {
    value: number
    suffix?: string
    duration?: number
    className?: string
}

// Counts from zero to the value when it scrolls into view. The server renders the final value.
export const CountUp: FC<CountUpProps> = ({ value, suffix = '', duration = 1400, className }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const inView = useInView(ref)
    const [display, setDisplay] = useState(value)
    const started = useRef(false)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        if (!inView) {
            if (!started.current) setDisplay(0)
            return
        }

        if (started.current) return

        started.current = true

        const start = performance.now()

        let frame = 0

        const step = (now: number): void => {
            const progress = Math.min(1, (now - start) / duration)
            const eased = 1 - Math.pow(1 - progress, 3)

            setDisplay(Math.round(value * eased))

            if (progress < 1) frame = requestAnimationFrame(step)
        }

        frame = requestAnimationFrame(step)

        return () => cancelAnimationFrame(frame)
    }, [inView, value, duration])

    return (
        <span ref={ref} className={className}>
            {display}
            {suffix}
        </span>
    )
}
